import telegram from "../../assets/telegram.svg";
import instagram from "../../assets/instagram.svg";
import whatsapp from "../../assets/whatsapp.svg";
import telegramBlack from "../../assets/telegram-black.svg";
import instagramBlack from "../../assets/instagram-black.svg";
import whatsappBlack from "../../assets/whatsapp-black.svg";
import TimeReadStatus from "./TimeReadStatus";
import { MessageResponse, MessageType } from "@/generated-api";
import { Button } from "../ui/button";
import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useDeleteMessage } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import SpinnerSmall from "../SpinnerSmall";
import { useTranslation } from "@/hooks/useTranslation";
import {
  openInstagramProfile,
  openTelegramProfile,
  openWhatsApp,
} from "@/lib/openExternal";

export default function ContactChatBubble({
  counterpartyId,
  message,
}: {
  counterpartyId: string;
  message: MessageResponse;
}) {
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const deleteMessageMutation = useDeleteMessage(counterpartyId);
  const counterparty = message.sender_id === counterpartyId;
  const t = useTranslation();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleDelete = () => {
    deleteMessageMutation.mutate(message.message_id, {
      onError: () => {
        toast({
          variant: "destructive",
          title: t.MESSAGES.ERRORS.DELETE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  const openSocialLink = () => {
    if (message.message_type === MessageType.TELEGRAM) {
      openTelegramProfile(message.content);
    }
    if (message.message_type === MessageType.INSTAGRAM) {
      openInstagramProfile(message.content);
    }
    if (message.message_type === MessageType.WHATSAPP) {
      openWhatsApp(message.content);
    }
  };

  const getIconSrc = () => {
    if (message.message_type === MessageType.TELEGRAM) {
      return counterparty ? telegramBlack : telegram;
    }
    if (message.message_type === MessageType.INSTAGRAM) {
      return counterparty ? instagramBlack : instagram;
    }
    if (message.message_type === MessageType.WHATSAPP) {
      return counterparty ? whatsappBlack : whatsapp;
    }
  };

  return (
    <div
      className={`${
        counterparty
          ? "bg-white text-black border-gray-500 border-2"
          : "bg-zinc-800 text-white border-white border"
      } px-3 py-2 rounded-2xl inline-flex items-center hover:cursor-pointer select-none`}
      onClick={openSocialLink}
    >
      <div className="relative">
        <div className="flex items-center gap-2 text-left max-w-xs lg:max-w-md mb-3">
          <div className="shrink-0">
            <img src={getIconSrc()} className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base break-words whitespace-pre-wrap">
              {(message.message_type === MessageType.TELEGRAM ||
                message.message_type === MessageType.INSTAGRAM) &&
                "@"}
              {message.content}
            </p>
          </div>
          {counterparty ? (
            <Button
              size="icon"
              className="text-black"
              variant="link"
              onClick={(e) => {
                e.stopPropagation();
                void handleCopy();
              }}
            >
              {isCopied ? <CheckIcon size={20} /> : <CopyIcon size={20} />}
            </Button>
          ) : (
            <Button
              size="icon"
              className="text-white"
              variant="link"
              onClick={(e) => {
                e.stopPropagation();
                void handleDelete();
              }}
            >
              {deleteMessageMutation.isPending ? (
                <SpinnerSmall />
              ) : (
                <XIcon size={20} />
              )}
            </Button>
          )}
        </div>
        <TimeReadStatus message={message} counterparty={counterparty} />
      </div>
    </div>
  );
}
